import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/use-page-meta";

const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="mt-10">
    <h2 className="font-heading text-lg font-bold tracking-tight text-[#0A0A0A]">
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#4B5563] md:text-base">
      {children}
    </div>
  </section>
);

export default function PrivacyPolicy() {
  usePageMeta(
    "Mu'min Kids Privacy Policy — Bright Frontier Labs",
    "Privacy Policy for Mu'min Kids, the ad-free Islamic learning app for children ages 3-10 published by Bright Frontier Labs. On-device learning data, no ads, no tracking, parent controls."
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid="privacy-policy-page"
    >
      <div className="bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-3xl px-6 py-20 md:px-8 md:py-24">
          <span className="text-xs font-semibold uppercase tracking-widest text-white/50">
            Legal &mdash; 01
          </span>
          <h1
            data-testid="privacy-heading"
            className="mt-6 font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Privacy Policy
          </h1>
          <p className="mt-4 text-sm text-white/50">
            Mu&rsquo;min Kids &middot; Last updated: September 21, 2026
          </p>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-6 py-16 md:px-8 md:py-20">
        <Section title="1. Introduction" testId="privacy-intro-section">
          <p>
            Mu&rsquo;min Kids is an Islamic learning app designed primarily for
            children ages 3&ndash;10 and their families, published by Bright
            Frontier Labs (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;).
            This Privacy Policy explains what information the app handles, where
            it is stored, and the choices parents have.
          </p>
          <p>
            The short version: children learn as guests, everything a child
            creates stays on the device, there are no ads and no tracking, and
            only parents can access account or subscription features.
          </p>
        </Section>

        <Section title="2. Learning Without an Account" testId="privacy-guest-section">
          <p>
            Core learning in Mu&rsquo;min Kids works as a guest &mdash; no child
            account is required. Everything related to a child&rsquo;s learning
            is stored locally on the device, including:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Child nickname, age group, and chosen avatar;</li>
            <li>Learning progress, rewards, and streaks;</li>
            <li>App settings and locally scheduled reminders;</li>
            <li>Temporary pronunciation recordings made during practice.</li>
          </ul>
          <p>
            Pronunciation recordings are never uploaded. They stay on the
            device and are deleted or replaced locally as practice continues.
            Android backup is disabled for the app, so this on-device data is
            not copied to cloud backups.
          </p>
        </Section>

        <Section title="3. What Mu'min Kids Does Not Do" testId="privacy-never-section">
          <p>Mu&rsquo;min Kids is built to be safe by default. The app:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Contains no advertisements and uses no advertising SDKs;</li>
            <li>Does not collect advertising identifiers;</li>
            <li>Uses no third-party product analytics;</li>
            <li>Does not track children across apps or websites;</li>
            <li>Has no chat, social features, or public profiles;</li>
            <li>Never sells personal data.</li>
          </ul>
        </Section>

        <Section title="4. Optional Parent Account" testId="privacy-parent-account-section">
          <p>
            Parents may optionally sign in to the parent-only area of the app.
            Parent sign-in uses Firebase Authentication and Google Sign-In.
            When a parent signs in, these services may process the parent&rsquo;s
            display name, email address, phone number, authentication UID, IP
            address and device metadata, security tokens, and sign-in events.
          </p>
          <p>
            This information is used solely to authenticate the parent and keep
            the account secure. Children cannot access or create these accounts.
          </p>
        </Section>

        <Section title="5. Optional Subscriptions" testId="privacy-subscriptions-section">
          <p>
            Optional Plus and Family subscriptions are purchased by parents
            through Google Play Billing or Apple StoreKit, with subscription
            management provided by RevenueCat. The app never receives card or
            bank details &mdash; payment is handled entirely by the store.
          </p>
          <p>
            To operate subscriptions, RevenueCat may process an app user ID,
            purchase history and receipts, entitlement and product status,
            locale and currency, and request metadata. This is used for billing,
            restoring purchases, fraud prevention, and subscription reporting.
          </p>
        </Section>

        <Section title="6. Quran Recitation Audio" testId="privacy-quran-audio-section">
          <p>
            Quran recitation audio that is not bundled with the app may be
            requested from everyayah.com when a recitation is played. Like any
            web service, everyayah.com can receive normal connection metadata
            such as IP address and user agent. We do not add a child&rsquo;s
            name, account ID, or advertising identifier to these requests.
          </p>
        </Section>

        <Section title="7. Data Security" testId="privacy-security-section">
          <p>
            The app is designed on-device first, which removes most risk by
            keeping children&rsquo;s data off our servers entirely. For the
            limited parent account and subscription data handled by our
            providers (Google Firebase, Google Play, Apple, RevenueCat),
            industry-standard technical and organizational safeguards apply.
            No method of transmission or storage is completely secure, but we
            choose providers and designs that minimize what is collected in the
            first place.
          </p>
        </Section>

        <Section title="8. Data Retention" testId="privacy-retention-section">
          <p>
            On-device learning data is kept until a parent clears it in Parent
            Controls or uninstalls the app. Parent account data is kept until
            the parent deletes the account in the app. Purchase and
            subscription records held by the stores or RevenueCat may be
            retained where legally required or where needed for purchase
            restoration and fraud prevention.
          </p>
        </Section>

        <Section title="9. International Processing" testId="privacy-international-section">
          <p>
            Our service providers (Google Firebase, Google Play, Apple, and
            RevenueCat) may process the limited data described above in
            countries other than your own. Where they do, they apply
            appropriate contractual and technical safeguards as described in
            their own privacy policies.
          </p>
        </Section>

        <Section title="10. Children's Privacy" testId="privacy-children-section">
          <p>
            Mu&rsquo;min Kids is made for children, with parents in control.
            Children use the app as guests; the personal data their learning
            generates stays on the family&rsquo;s device. Account and
            subscription features are gated behind parent-only flows, and we do
            not knowingly collect personal information from children off the
            device.
          </p>
        </Section>

        <Section title="11. Parent Rights and Controls" testId="privacy-rights-section">
          <p>Parents can, at any time:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Delete the parent account in-app via the Parent tab &rarr;
              Parent Account &rarr; Delete Parent Account;
            </li>
            <li>
              Clear all local learning data via the Parent tab &rarr; Privacy
              &amp; Data &rarr; Clear Learning Data;
            </li>
            <li>
              Request deletion or ask questions by emailing us &mdash; see our{" "}
              <Link
                to="/data-deletion"
                data-testid="privacy-data-deletion-link"
                className="font-medium text-[#0A0A0A] underline decoration-black/30 underline-offset-4 transition-colors duration-200 hover:decoration-black"
              >
                Data Deletion page
              </Link>{" "}
              for exact steps.
            </li>
          </ul>
          <p>
            Depending on where you live, you may also have additional legal
            rights to access, correct, or delete personal data. Contact us and
            we will help.
          </p>
        </Section>

        <Section title="12. Changes to This Policy" testId="privacy-changes-section">
          <p>
            We may update this Privacy Policy as the app evolves. The latest
            version will always be posted on this page with an updated
            &quot;Last updated&quot; date. Material changes will also be noted
            in the app where appropriate.
          </p>
        </Section>

        <Section title="13. Contact Us" testId="privacy-contact-section">
          <p>
            Questions about privacy, data, or deletion? Email us at{" "}
            <a
              href="mailto:support@brightfrontierlabs.com"
              data-testid="privacy-mailto-link"
              className="font-medium text-[#0A0A0A] underline decoration-black/30 underline-offset-4 transition-colors duration-200 hover:decoration-black"
            >
              support@brightfrontierlabs.com
            </a>
            . We aim to respond within two business days.
          </p>
        </Section>
      </div>
    </motion.div>
  );
}
